import { observer } from "mobx-react";
import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import userDetail from "Stores/userDetail";
import contactStoreData from "Stores/contactData";
import Utils from "Utils/utils";
import makeApi, { URLS } from "Utils/apiURL";
import SequenceUtils from "Utils/SequenceUtils";
import PageLimit from "Components/common/PageLimit";
import { toasterState } from "Components/common/toaster";
import AddToSequenceButton from "Components/common/AddToSequenceButton";
import EnrichBtn from "Components/common/EnrichBtn";
import ExportBtn from "Components/common/ExportBtn";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { updateContactOwnerState } from "Components/common/UpdateContactOwner";
import { enrichProcessingState } from "Components/popup/EnrichProcessing";
import { exportProcessingState } from "Components/popup/ExportProcessing";
import { addSequenceInContactPopupState } from "../../contacts/addSeqInContactPopup";
import { dataSearchState } from "../../dataSearch/state";
import { accountsState } from "../AccountsState";
import ContactsMoreOptions from "./ContactsMoreOptions";

const ContactsHeaderActions = observer((props = {}) => {
  const {
    tableData = {},
    accountId = "",
    tableLoading = false,
    allAccountsSelectedInView = false,
    startSequence = () => {},
    setShowMessage = () => {},
    getSelectedCount = () => {},
    setUploadingText = () => {},
    reloadContactsPage = () => {},
    resetAllStateValue = () => {},
    setFieldErrorDetails = () => {},
    clearSelectedContacts = () => {},
    setDailyLimitContactData = () => {},
    setContactUploadingLoader = () => {}
  } = props || {};
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showExportTypeDropdown, setShowExportTypeDropdown] = useState(false);

  const history = useHistory();

  const {
    emailValidationCredits = 0,
    emailValidationEnabled = false,
    exportEnabled = false
  } = userDetail?.userFeatureFlag || {};
  const { hasTeamMember = false } = userDetail?.userInfo || {};

  // page limit

  const getContactsByLimit = (limit = 10) => {
    resetAllStateValue();
    accountsState.setContactsState({ limit, pageNo: 1 });
  };

  // Add to sequence - start
  const hasContactChecked = () => {
    return getSelectedCount();
  };

  const getSelectedContactIds = useCallback(() => {
    return [...tableData?.contacts]
      .filter(item => item?.isChecked)
      .map(item => item?.id);
  }, [tableData?.contacts]);

  const getContactToSequencePayload = () => {
    let payload = {
      source: "ACCOUNTS",
      type: "ADD_TO_SEQUENCE",
      totalCount: getSelectedCount()
    };

    if (accountsState?.contactsState?.activeTab) {
      const { stageId = "", searchTerm = "" } =
        accountsState?.contactsState || {};
      payload.search = {
        searchTerm,
        stageId,
        filter: {
          accountId: [accountId]
        }
      };
      if (!allAccountsSelectedInView) {
        payload.contactIds = getSelectedContactIds();
      }
    }
    return payload;
  };

  const validateDailyLimitForContacts = async (payload = {}, title = "") => {
    let res = await contactStoreData.validateDailyLimitForContacts(payload);
    if (res && Object.keys(res)?.length) {
      let dataObj = res;
      dataObj = { ...dataObj, payload, title };
      setDailyLimitContactData(dataObj);
      const { contacts = [], fieldValidationResponse = {} } = res || {};
      const { missingFields = [], validCount = 0 } =
        fieldValidationResponse || {};
      if (
        fieldValidationResponse &&
        (missingFields?.length > 0 || !validCount)
      ) {
        fieldValidationResponse.contacts = contacts;
        setFieldErrorDetails(fieldValidationResponse);
        setContactUploadingLoader(false);
        setUploadingText("");
        setShowMessage(true);
      } else {
        setContactUploadingLoader(false);
        setUploadingText("");
        startSequence(dataObj);
        clearSelectedContacts();
      }
    }
  };
  const checkAndAddContactToSequence = (objProp = {}) => {
    let { title = "", payload = {}, sequenceId = "" } = objProp || {};
    payload = {
      ...payload,
      sequenceId
    };
    setUploadingText(`We are processing your contacts. Please wait.`);
    setContactUploadingLoader(true);
    validateDailyLimitForContacts(payload, title);
  };

  const addToSequenceAction = () => {
    if (hasContactChecked()) {
      const payload = getContactToSequencePayload();
      Utils.reloadFeatureFlag();
      const validateContactBeforeAddingToSequence = (
        data = {},
        sequenceId = "",
        currentSequence = {}
      ) => {
        if (currentSequence?.emailValidationEnabled) {
          let count = payload?.contactIds?.length
            ? payload?.contactIds?.length
            : payload?.totalCount || 0;
          if (emailValidationEnabled && emailValidationCredits < count) {
            SequenceUtils.promptEmailValidationNoCredits(
              count - emailValidationCredits,
              history,
              () =>
                checkAndAddContactToSequence({
                  ...data,
                  payload,
                  sequenceId
                }),
              () => {},
              "contact"
            );
            return;
          }
        }
        checkAndAddContactToSequence({ ...data, payload, sequenceId });
      };
      addSequenceInContactPopupState.setCallback(
        validateContactBeforeAddingToSequence
      );
      addSequenceInContactPopupState.setShowPopup(true);
    }
  };
  // end

  // Enrich - start
  const getEnrichPayload = (enrichContactPayload = {}) => {
    let payload = {
      source: "ACCOUNTS",
      type: "ENRICH_CONTACT",
      totalCount: getSelectedCount(),
      enrichContactPayload
    };
    const { stageId = "", searchTerm = "" } =
      accountsState?.contactsState || {};
    payload.search = {
      searchTerm,
      stageId,
      filter: { accountId: [accountId] }
    };
    if (!allAccountsSelectedInView) {
      payload.filter = { contactIds: getSelectedContactIds() };
    }
    return payload;
  };

  const enrichCbk = async (enrichContactPayload = {}) => {
    const { enrichType: type = "" } = enrichContactPayload;
    enrichProcessingState.setShowPopup(true);
    const payload = getEnrichPayload(enrichContactPayload);
    if (["EMAIL_PHONE", "PHONE"].includes(type)) {
      dataSearchState.setTotalEnrichContactsCount(payload.totalCount);
    }
    const config = {
      url: URLS.performContactsBulkOperation,
      method: "POST",
      data: payload
    };
    const response = await makeApi(config);
    enrichProcessingState.setShowPopup(false);
    if (response?.data) {
      const {
        success = false,
        background = false,
        bulkOperationResult = {},
        activeJob = false,
        responseCode,
        limit = 0
      } = response?.data || {};
      clearSelectedContacts();
      if (+responseCode === 207) {
        toasterState.setToastMsg(
          `Cannot select more than ${Utils.numberWithCommas(limit)} contacts for Enrich. Please try again.`,
          "failure"
        );
      } else if (activeJob) {
        SequenceUtils.showActiveJobPopupForBulkActions();
      } else if (success) {
        if (background) {
          confirmationPopupState.setPopupValues({
            content: "Contacts are being enriched; this may take a few minutes.",
            actionBtnText: "Ok",
            needCancelBtn: false,
            callback: () => confirmationPopupState.setShowPopup(false)
          });
          confirmationPopupState.setShowPopup(true);
        } else {
          const { success: enriched = 0 } = bulkOperationResult || {};
          toasterState.setToastMsg(
            `${enriched} contact${enriched > 1 ? "s" : ""} enriched successfully`,
            "success"
          );
          reloadContactsPage();
        }
      }
    } else {
      toasterState.setToastMsg("Something went wrong. Please try again later", "fail");
    }
  };
  // Enrich - end

  // Export - start
  const getExportPayload = () => {
    if (allAccountsSelectedInView) {
      const { stageId = "", searchTerm = "" } =
        accountsState?.contactsState || {};
      return {
        totalCount: tableData?.totalContacts || 0,
        all: false,
        search: {
          searchTerm,
          stageId,
          filter: { accountId: [accountId] }
        }
      };
    }
    return {
      contactList: getSelectedContactIds(),
      totalCount: getSelectedCount()
    };
  };

  const exportCbk = async (type = "") => {
    if (["csv"].includes(type?.toLowerCase())) {
      const payload = getExportPayload();
      exportProcessingState.setShowPopup(true);
      if (payload && Object.keys(payload)?.length) {
        const response = await Utils.exportContact(payload);
        if (response?.data) {
          const { downloadLink = "", background = false } = response?.data || {};
          if (background) {
            exportProcessingState.setShowPopup(false);
            confirmationPopupState.setPopupValues({
              content: "Your export is being processed. You will receive a notification when it's ready.",
              actionBtnText: "Ok",
              needCancelBtn: false,
              callback: () => confirmationPopupState.setShowPopup(false)
            });
            confirmationPopupState.setShowPopup(true);
          } else if (downloadLink) {
            exportProcessingState.completeAnimation();
            setTimeout(() => {
              Utils.downloadExportedContact(downloadLink);
              exportProcessingState.setShowPopup(false);
              clearSelectedContacts();
            }, 1500);
          } else {
            exportProcessingState.setShowPopup(false);
            toasterState.setToastMsg("Contact export failed", "failure");
          }
        } else {
          exportProcessingState.setShowPopup(false);
          clearSelectedContacts();
          toasterState.setToastMsg("Contact export failed", "failure");
        }
      }
    } else if (["hubspot"].includes(type?.toLowerCase())) {
      exportCrmContacts("HUBSPOT_CONTACT_EXPORT", type);
    } else if (["salesforce"].includes(type?.toLowerCase())) {
      exportCrmContacts("SALESFORCE_EXPORT", type);
    } else if (["pipedrive"].includes(type?.toLowerCase())) {
      exportCrmContacts("PIPEDRIVE_EXPORT", type);
    } else if (["zoho"].includes(type?.toLowerCase())) {
      exportCrmContacts("ZOHO_EXPORT", type);
    }
  };

  const exportCrmContacts = async (operationType = "", crmType = "") => {
    const payload = getBulkPerformActionPayload("", operationType);
    exportProcessingState.setShowPopup(true);
    const config = {
      url: URLS.performContactsBulkOperation,
      method: "POST",
      data: payload
    };
    const response = await makeApi(config);
    if (response?.data?.success) {
      clearSelectedContacts();
      const { background = false } = response?.data || {};
      if (background) {
        exportProcessingState.setShowPopup(false);
        confirmationPopupState.setPopupValues({
          content: `Contacts are being exported to ${crmType}; this may take a few minutes.`,
          actionBtnText: "Ok",
          needCancelBtn: false,
          callback: () => confirmationPopupState.setShowPopup(false)
        });
        confirmationPopupState.setShowPopup(true);
      } else {
        exportProcessingState.setShowPopup(false);
        toasterState.setToastMsg(`Contacts exported to ${crmType} successfully`, "success");
      }
    } else {
      exportProcessingState.setShowPopup(false);
      toasterState.setToastMsg("Something went wrong. Please try again later", "fail");
    }
  };
  // Export - end

  // Update owner - Starts here

  const expandMenu = () => {
    if (!tableLoading) {
      setShowActionsDropdown(true);
    }
  };

  const handleBulkUpdateStage = () => {};

  const getBulkPerformActionPayload = (ownerId, type = "") => {
    let payload = {
      source: "ACCOUNTS",
      type,
      totalCount: getSelectedCount(),
      newContactOwnerId: ownerId,
      filter: {}
    };
    const { stageId = "", searchTerm = "" } =
      accountsState?.contactsState || {};
    payload.search = {
      searchTerm,
      stageId
    };
    if (allAccountsSelectedInView) {
      payload.search.filter = {
        accountId: [accountId]
      };
    } else {
      payload.filter = {
        contactIds: getSelectedContactIds()
      };
    }
    return payload;
  };

  const showConfirmationPopupForBulkActions = (
    background,
    type = "",
    actionType = ""
  ) => {
    if (background) {
      confirmationPopupState.setPopupValues({
        content: `${type} are being updated; this may take a few to several minutes.`,
        actionBtnText: "Ok",
        needCancelBtn: false,
        callback: () => confirmationPopupState.setShowPopup(false)
      });
      confirmationPopupState.setShowPopup(true);
    } else {
      toasterState.setToastMsg(
        `${type} have been ${actionType} successfully`,
        "success"
      );
    }
  };

  const bulkUpdateContactOwner = async (ownerId = "") => {
    const data = getBulkPerformActionPayload(ownerId, "CONTACT_OWNER_UPDATE");
    if (ownerId && data) {
      const config = {
        url: URLS.performContactsBulkOperation,
        method: "POST",
        data
      };
      const response = await makeApi(config);
      updateContactOwnerState.setBtnLoader(false);
      if (response?.data) {
        const {
          success = false,
          background = false,
          activeJob = false
        } = response?.data || {};
        if (activeJob) {
          clearSelectedContacts();
          SequenceUtils.showActiveJobPopupForBulkActions();
        } else if (success) {
          clearSelectedContacts();
          if (!background) {
            reloadContactsPage();
          }
          showConfirmationPopupForBulkActions(
            background,
            "Contact owner",
            "updated"
          );
        } else {
          toasterState.setToastMsg(
            "Something went wrong. Please try again later",
            "fail"
          );
        }
      } else {
        toasterState.setToastMsg(
          "Something went wrong. Please try again later",
          "fail"
        );
      }
    } else {
      updateContactOwnerState.setBtnLoader(false);
    }
  };

  const openBulkUpdateOwnerPopup = () => {
    setShowActionsDropdown(false);
    updateContactOwnerState.setPopupType("CONTACT");
    updateContactOwnerState.setTotalCount(
      allAccountsSelectedInView
        ? accountsState.totalAccounts
        : getSelectedCount()
    );
    updateContactOwnerState.setCallback(bulkUpdateContactOwner);
    updateContactOwnerState.setShowPopup(true);
  };

  //obj goes here

  const pageLimitObj = {
    limitPerPage: accountsState?.contactsState?.limit || 10,
    text: "Showing",
    totalCount: tableData?.totalContacts || 0,
    cbk: getContactsByLimit
  };

  return (
    <>
      <PageLimit {...pageLimitObj} />
      {tableData?.contacts?.length > 0 && (
        <>
          <EnrichBtn
            selectedEntityCount={getSelectedCount() || 0}
            cbk={enrichCbk}
            getPreviewPayload={() => getEnrichPayload({})}
            loading={tableLoading}
            checked={hasContactChecked()}
            pageType="contacts"
            text="Enrich"
          />
          <AddToSequenceButton
            selectedEntityCount={getSelectedCount() || 0}
            cbk={addToSequenceAction}
            loading={tableLoading}
            checked={hasContactChecked()}
            text="Add to Sequence"
          />
          <ExportBtn
            selectedEntityCount={getSelectedCount() || 0}
            cbk={exportCbk}
            loading={tableLoading}
            isExportEnabled={exportEnabled}
            checked={hasContactChecked()}
            pageType="contacts"
            showExportTypeDropdown={showExportTypeDropdown}
            setShowExportTypeDropdown={setShowExportTypeDropdown}
            text="Export"
          />
        </>
      )}
      {tableData?.contacts?.length > 0 && hasTeamMember && (
        <ContactsMoreOptions
          showActionsDropdown={showActionsDropdown}
          setShowActionsDropdown={setShowActionsDropdown}
          expandMenu={expandMenu}
          updateAccountStage={handleBulkUpdateStage}
          getSelectedCount={getSelectedCount}
          updateAccountOwner={openBulkUpdateOwnerPopup}
          hasTeamMember={hasTeamMember}
        />
      )}
    </>
  );
});

export { ContactsHeaderActions };
export default ContactsHeaderActions;
